// MemoryDetail — right-side panel for viewing and editing a memory.

import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../api/client';
import TagPicker from './TagPicker';
import TagFriendPanel from './TagFriendPanel';
import VenuePicker from './VenuePicker';
import useDropdownKeyboard from '../hooks/useDropdownKeyboard';
import { tagNamesToPayload } from '../utils/tags';
import { countryFlag } from '../utils/countryFlag';

// All recognized country names for the datalist dropdown
const KNOWN_COUNTRIES = [
  'Afghanistan','Albania','Algeria','Argentina','Armenia','Australia','Austria',
  'Azerbaijan','Bahrain','Bangladesh','Belarus','Belgium','Bolivia','Brazil',
  'Bulgaria','Cambodia','Canada','Chile','China','Colombia','Costa Rica',
  'Croatia','Cuba','Cyprus','Czechia','Denmark','Ecuador','Egypt','Estonia',
  'Ethiopia','Finland','France','Georgia','Germany','Ghana','Greece','Hungary',
  'Iceland','India','Indonesia','Iran','Iraq','Ireland','Israel','Italy',
  'Jamaica','Japan','Jordan','Kazakhstan','Kenya','Kosovo','Kuwait','Latvia',
  'Lebanon','Lithuania','Luxembourg','Malaysia','Malta','Mexico','Moldova',
  'Mongolia','Montenegro','Morocco','Mozambique','Myanmar','Nepal','Netherlands',
  'New Zealand','Nicaragua','Nigeria','Norway','Oman','Pakistan','Panama',
  'Paraguay','Peru','Philippines','Poland','Portugal','Qatar','Romania',
  'Russia','Rwanda','Saudi Arabia','Senegal','Serbia','Singapore','Slovakia',
  'Slovenia','South Africa','South Korea','Spain','Sri Lanka','Sweden',
  'Switzerland','Syria','Taiwan','Tanzania','Thailand','Tunisia','Turkey',
  'Uganda','Ukraine','United Arab Emirates','United Kingdom','United States',
  'Uruguay','Uzbekistan','Venezuela','Vietnam','Yemen','Zambia','Zimbabwe',
  'Maldives','Laos','Palestine','Cambodia',
];

/**
 * Parse an aiSummary string into bullet items, or return null for plain text.
 */
function parseSummary(aiSummary) {
  if (!aiSummary) return null;
  const lines = aiSummary.split('\n').map(l => l.trim()).filter(Boolean);
  const bulletLines = lines.filter(l => l.startsWith('\u2022') || l.startsWith('-') || l.startsWith('*'));
  if (bulletLines.length >= 2) {
    return bulletLines.map(l => l.replace(/^[\u2022\-*]\s*/, ''));
  }
  return null;
}

// Track which pins are currently generating AI photos (persists across open/close)
const generatingPins = new Map(); // pinId → { generating: bool, subscribers: Set<fn> }

function useGeneratingPhoto(pinId) {
  const [generating, setGenerating] = useState(() => generatingPins.get(pinId)?.generating || false);

  useEffect(() => {
    if (!pinId) return;
    if (!generatingPins.has(pinId)) {
      generatingPins.set(pinId, { generating: false, subscribers: new Set() });
    }
    const entry = generatingPins.get(pinId);
    const subscriber = (val) => setGenerating(val);
    entry.subscribers.add(subscriber);
    setGenerating(entry.generating);
    return () => { entry.subscribers.delete(subscriber); };
  }, [pinId]);

  const setGen = useCallback((val) => {
    if (!pinId) return;
    const entry = generatingPins.get(pinId) || { generating: false, subscribers: new Set() };
    entry.generating = val;
    generatingPins.set(pinId, entry);
    entry.subscribers.forEach(fn => fn(val));
  }, [pinId]);

  return [generating, setGen];
}

export default function MemoryDetail({ pin, isOpen, onClose, onUpdated: _onUpdated, onPinChanged, rank, noBackdrop, readOnly, annotation }) {
  const [showTranscript, setShowTranscript] = useState(false);
  const [addition, setAddition] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saved, setSaved] = useState(false);
  const textareaRef = useRef(null);

  // Inline title edit
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleText, setTitleText] = useState(pin?.placeName || '');
  const [titleSaving, setTitleSaving] = useState(false);
  const titleInputRef = useRef(null);

  // Inline rating — always interactive
  const [liveRating, setLiveRating] = useState(0);
  const [ratingSaved, setRatingSaved] = useState(false);
  const ratingDebounceRef = useRef(null);

  // Highlights inline edit
  const [editingHighlights, setEditingHighlights] = useState(false);
  const [highlightsText, setHighlightsText] = useState('');
  const [highlightsSaving, setHighlightsSaving] = useState(false);
  const [highlightsError, setHighlightsError] = useState('');

  // Would-go-back toggle
  const [wouldGoBack, setWouldGoBack] = useState(pin?.wouldGoBack ?? null);
  const [wouldGoBackSaving, setWouldGoBackSaving] = useState(false);

  // Show-as-memory toggle — a trip is shown on the Memories board when it is
  // NOT a trip-log entry (is_trip_log = false). Inverted for display.
  const [showAsMemory, setShowAsMemory] = useState(!(pin?.isTripLog ?? false));
  const [showAsMemorySaving, setShowAsMemorySaving] = useState(false);

  // Details expander (year, companions, tags)
  const [showDetails, setShowDetails] = useState(false);
  const [editYear, setEditYear] = useState('');
  const [editMonth, setEditMonth] = useState('');
  const [editCompanions, setEditCompanions] = useState([]);
  const [editTags, setEditTags] = useState([]);
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [detailsSaving, setDetailsSaving] = useState(false);
  const [detailsError, setDetailsError] = useState('');
  const [detailsSaved, setDetailsSaved] = useState(false);

  // Places editor
  const [placesInput, setPlacesInput] = useState('');
  const [placesResults, setPlacesResults] = useState([]);
  const [placesLoading, setPlacesLoading] = useState(false);
  const [placesSaving, setPlacesSaving] = useState(false);
  const placesDebounceRef = useRef(null);

  // Countries picker
  const [countryInput, setCountryInput] = useState('');
  const [countrySaving, setCountrySaving] = useState(false);

  // Optimistic local state — updated instantly; synced to server in background
  const [localCountries, setLocalCountries] = useState(pin?.countries || []);
  const [localLocations, setLocalLocations] = useState(pin?.locations || []);
  const [localVenues, setLocalVenues] = useState(pin?.venues || []);

  // AI photo regeneration — state tracked per pin ID so switching pins doesn't bleed
  const [generatingPhoto, setGeneratingPhoto] = useGeneratingPhoto(pin?.id);
  const [localImageUrl, setLocalImageUrl] = useState(pin?.photoUrl || pin?.unsplashImageUrl || null);
  const [photoPromptOpen, setPhotoPromptOpen] = useState(false);
  const [photoQuery, setPhotoQuery] = useState('');

  // Multi-photo carousel (user-uploaded photos)
  const [photos, setPhotos] = useState(pin?.photos || []);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [openPhotoMenu, setOpenPhotoMenu] = useState(null); // photo.id whose kebab menu is open
  const fileInputRef = useRef(null);
  const carouselRef = useRef(null);
  const slideRefs = useRef({});

  // Inline "tag a friend" flow — state is now in TagFriendPanel; parent only tracks open/close
  const [showTagFriend, setShowTagFriend] = useState(false);

  // Companion search (used inside Edit details expander)
  const [companionSearch, setCompanionSearch] = useState('');
  const [companionResults, setCompanionResults] = useState([]);
  const [companionSearching, setCompanionSearching] = useState(false);
  const [showCompanionSearch, setShowCompanionSearch] = useState(false);
  const searchDebounceRef = useRef(null);

  // Seed state from pin
  useEffect(() => {
    if (pin) {
      setWouldGoBack(pin.wouldGoBack ?? null);
      setShowAsMemory(!(pin.isTripLog ?? false));
      setLiveRating(pin.rating || 0);
      setTitleText(pin.placeName || '');
      setEditingTitle(false);
      setEditYear(pin.visitYear ? String(pin.visitYear) : '');
      setEditMonth(pin.visitMonth ? String(pin.visitMonth) : '');
      setEditCompanions(pin.companions || []);
      setEditTags(pin.tags ? pin.tags.map(t => t.name || t) : []);
      setHighlightsText(pin.aiSummary || '');
      setLocalCountries(pin.countries || []);
      setLocalLocations(pin.locations || []);
      setLocalVenues(pin.venues || []);
      setLocalImageUrl(pin.photoUrl || pin.unsplashImageUrl || null);
      setPhotos(pin.photos || []);
      setPhotoIndex(0);

      // Fetch full pin data (includes locations and photos) if not already loaded
      if (!pin.locations || pin.locations.length === 0) {
        api.get(`/pins/${pin.id}`).then(res => {
          const full = res.data || res;
          if (full.locations && full.locations.length > 0) {
            setLocalLocations(full.locations);
          }
          if (full.photos && full.photos.length > 0) {
            setPhotos(full.photos);
          }
        }).catch(() => {});
      }
    }
  }, [pin?.id]);

  // Reset panel on open/close
  useEffect(() => {
    if (isOpen) {
      setShowTranscript(false);
      setAddition('');
      setSaving(false);
      setSaveError('');
      setSaved(false);
      setEditingHighlights(false);
      setHighlightsError('');
      setShowDetails(false);
      setDetailsError('');
      setDetailsSaved(false);
      setShowTagPicker(false);
      setShowCompanionSearch(false);
      setCompanionSearch('');
      setShowTagFriend(false);
      setPlacesInput('');
      setPlacesResults([]);
      setCountryInput('');
    }
  }, [isOpen, pin?.id]);

  // Debounced user search for companions
  useEffect(() => {
    if (!companionSearch.trim()) {
      setCompanionResults([]);
      return;
    }
    clearTimeout(searchDebounceRef.current);
    setCompanionSearching(true);
    searchDebounceRef.current = setTimeout(async () => {
      try {
        const res = await api.get(`/search/users?q=${encodeURIComponent(companionSearch.trim())}`);
        setCompanionResults(res.data || []);
      } catch {
        setCompanionResults([]);
      } finally {
        setCompanionSearching(false);
      }
    }, 350);
  }, [companionSearch]);

  // Debounced Google Places autocomplete
  useEffect(() => {
    // Use raw length so "New " (4 chars) isn't rejected by a trim check
    if (placesInput.length < 2) {
      setPlacesResults([]);
      return;
    }
    clearTimeout(placesDebounceRef.current);
    setPlacesLoading(true);
    placesDebounceRef.current = setTimeout(async () => {
      try {
        const res = await api.get(`/places/autocomplete?q=${encodeURIComponent(placesInput.trim())}`);
        setPlacesResults(Array.isArray(res) ? res : (res.data || res.suggestions || []));
      } catch {
        setPlacesResults([]);
      } finally {
        setPlacesLoading(false);
      }
    }, 350);
  }, [placesInput]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    function onKey(e) {
      if (e.key === 'Escape') {
        if (showTagFriend) { setShowTagFriend(false); return; }
        if (editingHighlights) { setEditingHighlights(false); return; }
        if (showDetails) { setShowDetails(false); return; }
        onClose();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, showTagFriend, editingHighlights, showDetails]);

  // Keyboard navigation hooks — must be before early return
  const countryKbCount = countryInput.trim().length > 0
    ? KNOWN_COUNTRIES.filter(c => c.toLowerCase().includes(countryInput.toLowerCase())).length
    : 0;
  const countryKb = useDropdownKeyboard(countryKbCount, () => {}, () => setCountryInput(''));
  const placesKb = useDropdownKeyboard(placesResults.length, () => {}, () => { setPlacesInput(''); setPlacesResults([]); });

  if (!pin) return null;

  const summaryBullets = parseSummary(pin.aiSummary);

  // ---- Places helpers ----
  async function handleSelectPlace(suggestion) {
    if (placesSaving) return;
    // Use short mainText for display (e.g. "El Calafate"), full description for geocoding
    const displayName = suggestion.mainText || suggestion.description || '';
    const tempId = `temp-${Date.now()}`;
    const prevLocations = localLocations;
    // Optimistic: show immediately
    setLocalLocations(prev => [...prev, { id: tempId, placeName: displayName, normalizedCountry: null }]);
    setPlacesInput('');
    setPlacesResults([]);
    setPlacesSaving(true);
    try {
      // Send full description to server for accurate geocoding, but store mainText as display name
      const res = await api.post(`/pins/${pin.id}/locations`, { placeName: displayName });
      const real = res.data || res;
      const updated = [...prevLocations, real];
      setLocalLocations(updated);
      if (onPinChanged) onPinChanged(pin.id, { locations: updated });
    } catch {
      setLocalLocations(prevLocations); // revert
    } finally {
      setPlacesSaving(false);
    }
  }

  async function handleAddPlaceManual() {
    const name = placesInput.trim();
    if (!name || placesSaving) return;
    const tempId = `temp-${Date.now()}`;
    const prevLocations = localLocations;
    // Optimistic: show immediately
    setLocalLocations(prev => [...prev, { id: tempId, placeName: name, normalizedCountry: null }]);
    setPlacesInput('');
    setPlacesResults([]);
    setPlacesSaving(true);
    try {
      const res = await api.post(`/pins/${pin.id}/locations`, { placeName: name });
      const real = res.data || res;
      const updated = [...prevLocations, real];
      setLocalLocations(updated);
      if (onPinChanged) onPinChanged(pin.id, { locations: updated });
    } catch {
      setLocalLocations(prevLocations); // revert
    } finally {
      setPlacesSaving(false);
    }
  }

  async function handleRemovePlace(locId) {
    const prevLocations = localLocations;
    // Optimistic: remove immediately
    setLocalLocations(prev => prev.filter(l => l.id !== locId));
    try {
      await api.delete(`/pins/${pin.id}/locations/${locId}`);
      const updated = prevLocations.filter(l => l.id !== locId);
      if (onPinChanged) onPinChanged(pin.id, { locations: updated });
    } catch {
      setLocalLocations(prevLocations); // revert
    }
  }

  // ---- Countries helpers ----
  // Union all country sources: local optimistic array + normalizedCountry + location countries
  const effectiveCountries = Array.from(new Set([
    ...localCountries,
    ...(pin.normalizedCountry ? [pin.normalizedCountry] : []),
    ...localLocations.map(l => l.normalizedCountry).filter(Boolean),
  ]));

  const countryMatches = countryInput.trim().length > 0
    ? KNOWN_COUNTRIES.filter(c => {
        const q = countryInput.toLowerCase();
        return c.toLowerCase().includes(q) && !effectiveCountries.includes(c);
      }).slice(0, 8)
    : [];

  async function handleAddCountry(country) {
    if (countrySaving) return;
    if (effectiveCountries.includes(country)) { setCountryInput(''); return; }
    const prev = localCountries;
    const updated = [...prev, country];
    // Optimistic: add immediately
    setLocalCountries(updated);
    setCountryInput('');
    setCountrySaving(true);
    try {
      await api.put(`/pins/${pin.id}`, { countries: updated });
      if (onPinChanged) onPinChanged(pin.id, { countries: updated });
    } catch {
      setLocalCountries(prev); // revert
    } finally {
      setCountrySaving(false);
    }
  }

  async function handleRemoveCountry(country) {
    if (countrySaving) return;
    const prev = localCountries;
    const updated = prev.filter(c => c !== country);
    // Optimistic: remove immediately
    setLocalCountries(updated);
    setCountrySaving(true);
    try {
      const payload = { countries: updated };
      if (pin.normalizedCountry === country) payload.normalizedCountry = null;
      await api.put(`/pins/${pin.id}`, payload);
      if (onPinChanged) onPinChanged(pin.id, { countries: updated });
    } catch {
      setLocalCountries(prev); // revert
    } finally {
      setCountrySaving(false);
    }
  }


  // ---- Venues (national parks / ski resorts) ----
  async function handleVenuesChange(updated) {
    const prev = localVenues;
    setLocalVenues(updated);
    try {
      await api.put(`/pins/${pin.id}`, { venues: updated.map(v => v.id) });
      if (onPinChanged) onPinChanged(pin.id, { venues: updated });
    } catch {
      setLocalVenues(prev);
    }
  }

  // ---- Rating (always interactive, auto-save) ----
  async function handleRatingClick(v) {
    if (guardEdit()) return;
    const next = v === liveRating ? 0 : v;
    setLiveRating(next);
    clearTimeout(ratingDebounceRef.current);
    ratingDebounceRef.current = setTimeout(async () => {
      try {
        await api.put(`/pins/${pin.id}`, { rating: next || null });
        setRatingSaved(true);
        setTimeout(() => setRatingSaved(false), 1500);
        if (onPinChanged) onPinChanged(pin.id, { rating: next || null });
      } catch { /* silent */ }
    }, 400);
  }

  // ---- Would-go-back toggle ----
  async function handleWouldGoBack(next) {
    if (guardEdit()) return;
    setWouldGoBack(next);
    setWouldGoBackSaving(true);
    try {
      await api.put(`/pins/${pin.id}`, { wouldGoBack: next });
      if (onPinChanged) onPinChanged(pin.id, { wouldGoBack: next });
    } catch {
      setWouldGoBack(wouldGoBack); // revert
    } finally {
      setWouldGoBackSaving(false);
    }
  }

  // ---- Show-as-memory toggle ----
  // next = whether the trip should appear on the Memories board.
  // Persisted as is_trip_log = !next.
  async function handleShowAsMemory(next) {
    if (guardEdit()) return;
    const prev = showAsMemory;
    setShowAsMemory(next);
    setShowAsMemorySaving(true);
    try {
      await api.put(`/pins/${pin.id}`, { isTripLog: !next });
      if (onPinChanged) onPinChanged(pin.id, { isTripLog: !next });
    } catch {
      setShowAsMemory(prev); // revert
    } finally {
      setShowAsMemorySaving(false);
    }
  }

  // ---- Read-only guard — blocks all mutations when viewing someone else's pin ----
  function guardEdit() { return !!readOnly; }

  // ---- Archive pin ----
  async function handleArchive() {
    if (guardEdit()) return;
    if (!window.confirm('Archive this memory? It won\'t appear on your board but can be restored later.')) return;
    try {
      await api.put(`/pins/${pin.id}`, { archived: true });
      if (onPinChanged) onPinChanged(pin.id, { archived: true });
      onClose();
    } catch { /* silent */ }
  }

  // ---- Title save ----
  async function handleSaveTitle() {
    if (guardEdit()) return;
    const trimmed = titleText.trim();
    if (!trimmed || trimmed === pin.placeName) {
      setEditingTitle(false);
      setTitleText(pin.placeName || '');
      return;
    }
    setTitleSaving(true);
    try {
      await api.put(`/pins/${pin.id}`, { placeName: trimmed });
      if (onPinChanged) onPinChanged(pin.id, { placeName: trimmed });
      setEditingTitle(false);
    } catch { /* silent */ } finally {
      setTitleSaving(false);
    }
  }

  // ---- Highlights save ----
  async function handleSaveHighlights() {
    if (guardEdit()) return;
    setHighlightsSaving(true);
    setHighlightsError('');
    try {
      await api.put(`/pins/${pin.id}`, { aiSummary: highlightsText });
      setEditingHighlights(false);
      if (onPinChanged) onPinChanged(pin.id, { aiSummary: highlightsText });
    } catch (err) {
      setHighlightsError(err.message || 'Could not save.');
    } finally {
      setHighlightsSaving(false);
    }
  }

  // ---- Details save ----
  async function handleSaveDetails() {
    if (guardEdit()) return;
    setDetailsSaving(true);
    setDetailsError('');
    try {
      const tagPayload = tagNamesToPayload(editTags);
      await api.put(`/pins/${pin.id}`, {
        visitYear: editYear ? parseInt(editYear, 10) : null,
        visitMonth: editMonth ? parseInt(editMonth, 10) : null,
        companions: editCompanions,
        tags: tagPayload,
      });
      setDetailsSaved(true);
      setTimeout(() => setDetailsSaved(false), 1500);
      setShowDetails(false);
      setShowTagPicker(false);
      setShowCompanionSearch(false);
      setCompanionSearch('');
      if (onPinChanged) onPinChanged(pin.id, {
        visitYear: editYear ? parseInt(editYear, 10) : null,
        visitMonth: editMonth ? parseInt(editMonth, 10) : null,
        companions: editCompanions,
      });
    } catch (err) {
      setDetailsError(err.message || 'Could not save changes.');
    } finally {
      setDetailsSaving(false);
    }
  }

  // ---- Inline visit date save (year + month at top of detail) ----
  async function saveVisitDate(year, month) {
    if (guardEdit()) return;
    const visitYear = year ? parseInt(year, 10) : null;
    const visitMonth = month ? parseInt(month, 10) : null;
    try {
      await api.put(`/pins/${pin.id}`, { visitYear, visitMonth });
      if (onPinChanged) onPinChanged(pin.id, { visitYear, visitMonth });
    } catch (err) {
      console.error('Failed to save visit date', err);
    }
  }

  // ---- Add note ----
  async function handleSaveAddition() {
    if (guardEdit()) return;
    // Empty addition: nothing to append, but treat the click as
    // "save & exit" — close the panel without a server round-trip.
    // (Previously the button was disabled and silently swallowed taps.)
    if (!addition.trim()) {
      if (onClose) onClose();
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      const existing = pin.note || '';
      const updated = existing ? existing + '\n\n' + addition.trim() : addition.trim();
      await api.put(`/pins/${pin.id}`, { note: updated });
      setSaved(true);
      setAddition('');
      setTimeout(() => setSaved(false), 2000);
      if (onPinChanged) onPinChanged(pin.id, { note: updated });
    } catch (err) {
      setSaveError(err.message || 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  // ---- Fetch Unsplash photo ----

  async function handleUnsplashPhoto(customQuery) {
    if (guardEdit()) return;
    if (generatingPhoto) return;
    setPhotoPromptOpen(false);
    setPhotoQuery('');
    const currentPinId = pin.id;
    setGeneratingPhoto(true);
    try {
      const body = customQuery ? { query: customQuery } : {};
      const res = await api.post(`/pins/${currentPinId}/unsplash-photo`, body);
      const data = res.data || res;
      const newUrl = data.unsplashImageUrl;
      if (newUrl) {
        if (pin?.id === currentPinId) setLocalImageUrl(newUrl);
        if (onPinChanged) onPinChanged(currentPinId, {
          unsplashImageUrl: newUrl,
          unsplashAttribution: data.unsplashAttribution,
          photoUrl: null,
          photoSource: 'unsplash',
        });
      }
    } catch { /* silent */ } finally {
      const entry = generatingPins.get(currentPinId);
      if (entry) {
        entry.generating = false;
        entry.subscribers.forEach(fn => fn(false));
      }
    }
  }

  // ---- Multi-photo upload/delete handlers ----

  async function handlePhotoUpload(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingPhotos(true);
    try {
      const formData = new FormData();
      files.forEach(f => formData.append('photos', f));
      const res = await api.postFormData(`/pins/${pin.id}/photos`, formData);
      const data = res.data || res;
      const updated = data.photos || [];
      setPhotos(updated);
      setPhotoIndex(Math.max(0, updated.length - files.length));
      if (onPinChanged) onPinChanged(pin.id, { photos: updated });
    } catch (err) {
      console.error('Photo upload failed:', err);
    } finally {
      setUploadingPhotos(false);
      e.target.value = '';
    }
  }

  async function handlePhotoDelete(photoId) {
    setOpenPhotoMenu(null);
    // Optimistic: remove from local list immediately
    const prevPhotos = photos;
    const optimistic = photos.filter(p => p.id !== photoId);
    setPhotos(optimistic);
    setPhotoIndex(prev => Math.max(0, Math.min(prev, optimistic.length - 1)));
    try {
      const res = await api.delete(`/pins/${pin.id}/photos/${photoId}`);
      const data = res.data || res;
      const updated = data.photos || optimistic;
      setPhotos(updated);
      setPhotoIndex(prev => Math.max(0, Math.min(prev, updated.length - 1)));
      if (onPinChanged) onPinChanged(pin.id, { photos: updated });
    } catch (err) {
      console.error('Photo delete failed:', err);
      // Revert on failure
      setPhotos(prevPhotos);
    }
  }

  // Set a carousel photo as the pin's cover image. Optimistic + persisted via PUT /pins/:id.
  async function handleSetCover(photo) {
    setOpenPhotoMenu(null);
    if (!photo || !photo.photoUrl) return;
    const payload = {
      photoUrl: photo.photoUrl,
      photoSource: photo.photoSource || 'upload',
      unsplashImageUrl: null,
      unsplashAttribution: null,
    };
    const prevCover = localImageUrl;
    setLocalImageUrl(photo.photoUrl);
    try {
      await api.put(`/pins/${pin.id}`, payload);
      if (onPinChanged) onPinChanged(pin.id, payload);
    } catch (err) {
      console.error('Set cover failed:', err);
      setLocalImageUrl(prevCover);
    }
  }

  // Order photos so the active cover comes first; the rest keep their stable order.
  // Mirrored by client/src/tests/MemoryPhotos.test.jsx — keep in sync.
  function orderPhotosCoverFirst(list, coverUrl) {
    if (!Array.isArray(list) || list.length < 2 || !coverUrl) return list || [];
    const idx = list.findIndex(p => p && p.photoUrl === coverUrl);
    if (idx <= 0) return list;
    return [list[idx], ...list.slice(0, idx), ...list.slice(idx + 1)];
  }

  // ---- Companion helpers ----
  function isPresetActive(label) { return editCompanions.includes(label); }
  function togglePreset(label) {
    setEditCompanions(prev =>
      prev.includes(label) ? prev.filter(x => x !== label) : [...prev, label]
    );
  }
  function removeCompanion(name) {
    setEditCompanions(prev => prev.filter(x => x !== name));
  }
  function addUserCompanion(user) {
    const label = user.display_name || user.username;
    if (!editCompanions.includes(label)) {
      setEditCompanions(prev => [...prev, label]);
    }
    setCompanionSearch('');
    setCompanionResults([]);
    setShowCompanionSearch(false);
  }

  const noCompanionResults = companionSearch.trim().length > 1 && !companionSearching && companionResults.length === 0;

  return (
    <>
      <style>{`
        /* ---- AI photo regenerate ---- */
        .md-regen-photo-btn {
          position: absolute; bottom: 10px; right: 10px;
          display: inline-flex; align-items: center; gap: 5px;
          padding: 5px 10px; border-radius: 20px;
          background: rgba(10,10,10,0.65); backdrop-filter: blur(6px);
          border: 1px solid rgba(255,255,255,0.15);
          color: rgba(255,255,255,0.85); font-size: 11px; font-weight: 500;
          letter-spacing: 0.04em; cursor: pointer;
          transition: background 0.15s, opacity 0.15s;
          z-index: 10;
        }
        .md-regen-photo-btn:hover:not(:disabled) { background: rgba(30,30,30,0.85); }
        .md-regen-photo-btn:disabled { opacity: 0.6; cursor: default; }
        .md-regen-spinner {
          width: 11px; height: 11px; border-radius: 50%;
          border: 1.5px solid rgba(255,255,255,0.3);
          border-top-color: white;
          animation: md-spin 0.7s linear infinite;
        }
        @keyframes md-spin { to { transform: rotate(360deg); } }
        .md-hero-generating { animation: md-pulse 3s ease-in-out infinite; }
        @keyframes md-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.55; }
        }
        /* ---- Countries & Places pickers ---- */
        .md-picker-section { margin-bottom: 16px; }
        .md-chip-list {
          display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 8px;
        }
        .md-picker-chip {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 4px 8px 4px 10px; border-radius: 16px;
          background: var(--surface-2); border: 1px solid var(--border);
          color: var(--text-primary); font-size: 13px;
        }
        .md-picker-chip-flag { font-size: 15px; line-height: 1; }
        .md-picker-chip-remove {
          background: none; border: none; cursor: pointer;
          color: var(--text-muted); padding: 0 0 0 2px;
          font-size: 14px; line-height: 1; transition: color 0.12s;
        }
        .md-picker-chip-remove:hover { color: var(--text-primary); }
        /* Input + dropdown wrapper */
        .md-picker-input-wrap { position: relative; }
        .md-picker-input {
          width: 100%; background: var(--surface);
          border: 1px solid var(--border-strong); border-radius: 4px;
          color: var(--text-primary); padding: 8px 12px; font-size: 13px;
          outline: none; box-sizing: border-box; transition: border-color 0.18s;
          font-family: 'Inter', system-ui, sans-serif;
        }
        .md-picker-input:focus { border-color: var(--gold); }
        .md-picker-input::placeholder { color: var(--text-muted); }
        .md-picker-dropdown {
          position: absolute; top: calc(100% + 4px); left: 0; right: 0;
          background: var(--surface); border: 1px solid var(--border);
          border-radius: 4px; overflow: hidden; z-index: 300;
          box-shadow: 0 8px 24px rgba(10,10,10,0.12);
        }
        .md-picker-option {
          display: flex; align-items: center; gap: 8px;
          padding: 9px 13px; cursor: pointer; font-size: 13px;
          color: var(--text-primary); transition: background 0.12s;
        }
        .md-picker-option:hover, .md-picker-option-highlighted { background: var(--surface-2); }
        .md-picker-option-flag { font-size: 16px; line-height: 1; flex-shrink: 0; }
        .md-picker-option-icon { font-size: 14px; flex-shrink: 0; color: var(--text-muted); }
        .md-picker-option-main { font-weight: 500; }
        .md-picker-option-sub { color: var(--text-muted); font-size: 12px; }
        .md-picker-loading { padding: 10px 13px; font-size: 12px; color: var(--text-muted); }

        /* ---- Tag a friend (inline, read-view) ---- */
        .md-tag-friend-row {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
          margin-top: 4px;
        }
        .md-tag-friend-btn {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 5px 12px;
          border-radius: 2px;
          border: 1px dashed var(--border-strong);
          background: transparent;
          color: var(--text-secondary);
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.07em;
          text-transform: uppercase;
          cursor: pointer;
          transition: all 0.18s;
        }
        .md-tag-friend-btn:hover {
          border-color: var(--gold);
          color: var(--gold);
          background: rgba(201,168,76,0.04);
        }
        .md-tf-wrap {
          border: 1px solid var(--border);
          border-radius: 4px;
          background: var(--surface-2);
          padding: 12px 14px;
          margin-top: 8px;
        }
        .md-tf-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 10px;
        }
        .md-tf-title {
          font-size: 10px;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--text-muted);
          font-weight: 600;
        }
        .md-tf-close {
          background: none; border: none; cursor: pointer;
          color: var(--text-muted); font-size: 16px; padding: 0;
          line-height: 1; transition: color 0.15s;
        }
        .md-tf-close:hover { color: var(--text-primary); }
        /* Replaced the × close with a "Done" text button — clearer
           affordance for the multi-tag flow where the user explicitly
           ends their tagging session. */
        .md-tf-close-btn {
          background: none;
          border: 1px solid var(--gold);
          color: var(--gold);
          font-size: 11px; font-weight: 600;
          letter-spacing: 0.08em; text-transform: uppercase;
          padding: 4px 12px; border-radius: 14px;
          cursor: pointer;
          transition: all 0.15s;
        }
        .md-tf-close-btn:hover {
          background: var(--gold); color: #000;
        }
        /* Inline confirmation banner shown after each tag/invite */
        .md-tf-flash {
          font-size: 12px;
          padding: 7px 10px;
          border-radius: 4px;
          margin-bottom: 10px;
          line-height: 1.4;
        }
        .md-tf-flash-tagged {
          background: rgba(201,168,76,0.12);
          border: 1px solid rgba(201,168,76,0.4);
          color: var(--text-primary);
        }
        .md-tf-flash-already {
          background: var(--surface);
          border: 1px solid var(--border);
          color: var(--text-secondary);
        }
        .md-tf-flash-invited {
          background: rgba(82,168,98,0.10);
          border: 1px solid rgba(82,168,98,0.4);
          color: var(--text-primary);
        }
        /* Share-by-URL row */
        .md-tf-share-row {
          display: flex; gap: 8px; align-items: center;
          margin-bottom: 10px; flex-wrap: wrap;
        }
        .md-tf-share-btn {
          background: transparent;
          border: 1px solid var(--gold);
          color: var(--gold);
          padding: 6px 12px; border-radius: 14px;
          font-size: 11px; font-weight: 600;
          letter-spacing: 0.06em; text-transform: uppercase;
          cursor: pointer;
          transition: all 0.15s;
        }
        .md-tf-share-btn:hover { background: var(--gold); color: #000; }
        .md-tf-share-url {
          flex: 1; min-width: 200px;
          background: var(--surface);
          border: 1px solid var(--border);
          color: var(--text-primary);
          font-size: 12px;
          padding: 5px 10px; border-radius: 4px;
          font-family: 'Inter', monospace;
        }
        /* Pending invites list */
        .md-tf-pending {
          margin-bottom: 12px;
          padding: 8px 10px;
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: 4px;
        }
        .md-tf-pending-title {
          font-size: 10px; letter-spacing: 0.12em; text-transform: uppercase;
          color: var(--text-muted); font-weight: 600;
          margin: 0 0 6px;
        }
        .md-tf-pending-row {
          display: flex; align-items: center; justify-content: space-between;
          gap: 10px;
          padding: 4px 0;
          font-size: 13px;
          color: var(--text-primary);
        }
        .md-tf-pending-row + .md-tf-pending-row {
          border-top: 1px solid var(--border);
        }
        .md-tf-pending-label {
          flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .md-tf-pending-sub {
          color: var(--text-muted); font-size: 11px;
        }
        .md-tf-pending-actions {
          display: inline-flex; gap: 6px; align-items: center;
        }
        .md-tf-pending-btn {
          background: transparent;
          border: 1px solid var(--border-strong);
          color: var(--text-secondary);
          font-size: 10px; font-weight: 600;
          letter-spacing: 0.06em; text-transform: uppercase;
          padding: 3px 8px; border-radius: 10px;
          cursor: pointer;
          transition: all 0.15s;
        }
        .md-tf-pending-btn:hover { border-color: var(--gold); color: var(--gold); }
        .md-tf-pending-cancel {
          padding: 3px 7px;
          font-size: 13px;
          line-height: 1;
          letter-spacing: 0;
        }
        .md-tf-search-wrap { position: relative; }
        .md-tf-input {
          width: 100%;
          background: var(--surface);
          border: 1px solid var(--border-strong);
          border-radius: 4px;
          color: var(--text-primary);
          padding: 9px 12px;
          font-size: 14px;
          outline: none;
          box-sizing: border-box;
          transition: border-color 0.18s;
          font-family: 'Inter', system-ui, sans-serif;
        }
        .md-tf-input:focus { border-color: var(--gold); }
        .md-tf-input::placeholder { color: var(--text-muted); }
        .md-tf-dropdown {
          position: absolute; top: calc(100% + 4px); left: 0; right: 0;
          background: var(--surface); border: 1px solid var(--border);
          border-radius: 4px; overflow: hidden; z-index: 300;
          box-shadow: 0 8px 24px rgba(10,10,10,0.12);
        }
        .md-tf-result {
          display: flex; align-items: center; gap: 10px;
          padding: 10px 14px; cursor: pointer; transition: background 0.12s;
        }
        .md-tf-result:hover, .md-tf-result-highlighted { background: var(--surface-2); }
        .md-tf-avatar {
          width: 34px; height: 34px; border-radius: 50%;
          background: var(--surface-3); border: 1px solid var(--border);
          display: flex; align-items: center; justify-content: center;
          font-size: 14px; font-weight: 600;
          overflow: hidden; flex-shrink: 0;
          color: var(--gold-dim);
        }
        .md-tf-avatar img { width: 100%; height: 100%; object-fit: cover; }
        .md-tf-name { font-size: 14px; color: var(--text-primary); font-weight: 500; }
        .md-tf-username { font-size: 12px; color: var(--text-muted); margin-top: 1px; }
        .md-tf-no-results {
          padding: 10px 14px; font-size: 13px; color: var(--text-muted);
        }
        .md-tf-invite-prompt { margin-top: 10px; }
        .md-tf-invite-label {
          font-size: 12px; color: var(--text-secondary); margin-bottom: 8px; line-height: 1.5;
        }
        .md-tf-invite-label strong { color: var(--text-primary); }
        .md-tf-invite-row { display: flex; gap: 8px; }
        .md-tf-email-input {
          flex: 1;
          background: var(--surface);
          border: 1px solid var(--border-strong);
          border-radius: 4px;
          color: var(--text-primary);
          padding: 8px 12px;
          font-size: 13px;
          outline: none;
          transition: border-color 0.18s;
          font-family: 'Inter', system-ui, sans-serif;
        }
        .md-tf-email-input:focus { border-color: var(--gold); }
        .md-tf-email-input::placeholder { color: var(--text-muted); }
        .md-tf-send-btn {
          padding: 8px 16px; border-radius: 2px;
          background: var(--black); color: var(--surface);
          border: none; font-size: 11px; font-weight: 600;
          cursor: pointer; white-space: nowrap; letter-spacing: 0.08em;
          text-transform: uppercase;
        }
        .md-tf-send-btn:hover { background: #222; }
        .md-tf-send-btn:disabled { opacity: 0.4; cursor: default; }
        .md-tf-done {
          display: flex; align-items: center; gap: 8px;
          padding: 10px 0 2px;
          font-size: 13px; color: var(--gold-dim);
        }
        .md-tf-searching { padding: 10px 14px; font-size: 13px; color: var(--text-muted); }

        /* ---- Inline hearts ---- */
        .md-hearts-row {
          display: flex;
          align-items: center;
          gap: 2px;
        }
        .md-heart-btn {
          background: none;
          border: none;
          cursor: pointer;
          font-size: 18px;
          padding: 2px 3px;
          opacity: 0.3;
          transition: opacity 0.15s, transform 0.12s;
          line-height: 1;
        }
        .md-heart-btn.filled { opacity: 1; }
        .md-heart-btn:hover { opacity: 0.85; transform: scale(1.15); }
        .md-rating-saved {
          font-size: 11px;
          color: var(--gold-dim);
          margin-left: 6px;
          letter-spacing: 0.04em;
          animation: mdFadeIn 0.2s ease;
        }
        @keyframes mdFadeIn { from { opacity: 0; } to { opacity: 1; } }

        /* ---- Highlights inline edit ---- */
        .md-highlights-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 8px;
        }
        .md-highlights-edit-btn {
          background: var(--surface-2);
          border: 1px solid var(--border-strong);
          cursor: pointer;
          color: var(--text-secondary);
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          padding: 4px 12px;
          border-radius: 2px;
          transition: all 0.15s;
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .md-highlights-edit-btn:hover {
          color: var(--gold-dim);
          border-color: var(--gold);
          background: rgba(201,168,76,0.06);
        }
        .md-highlights-textarea {
          width: 100%;
          background: var(--surface);
          border: 1px solid var(--border-strong);
          border-radius: 4px;
          color: var(--text-primary);
          padding: 10px 12px;
          font-size: 14px;
          line-height: 1.6;
          font-family: 'Inter', system-ui, sans-serif;
          resize: vertical;
          outline: none;
          box-sizing: border-box;
          min-height: 80px;
        }
        .md-highlights-textarea:focus { border-color: var(--gold); }
        .md-highlights-actions {
          display: flex;
          gap: 8px;
          align-items: center;
          margin-top: 8px;
        }
        .md-hl-save-btn {
          padding: 7px 18px;
          border-radius: 2px;
          background: var(--black);
          color: var(--surface);
          border: none;
          font-size: 11px;
          font-weight: 600;
          cursor: pointer;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }
        .md-hl-save-btn:hover { background: #222; }
        .md-hl-save-btn:disabled { opacity: 0.4; cursor: default; }
        .md-hl-cancel-btn {
          padding: 7px 14px;
          border-radius: 2px;
          background: none;
          color: var(--text-muted);
          border: 1px solid var(--border-strong);
          font-size: 11px;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          cursor: pointer;
          transition: color 0.15s, border-color 0.15s;
        }
        .md-hl-cancel-btn:hover { color: var(--text-primary); border-color: var(--border-strong); }
        .md-hl-error { font-size: 12px; color: #C0392B; }

        /* ---- Edit details expander ---- */
        .md-details-toggle {
          display: flex;
          align-items: center;
          gap: 6px;
          background: none;
          border: none;
          cursor: pointer;
          color: var(--text-muted);
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          padding: 6px 0;
          transition: color 0.15s;
          margin-top: 8px;
        }
        .md-details-toggle:hover { color: var(--text-secondary); }
        .md-details-toggle svg { transition: transform 0.2s; }
        .md-details-toggle.open svg { transform: rotate(180deg); }
        .md-details-expand {
          border-top: 1px solid var(--border);
          padding-top: 16px;
          margin-top: 12px;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        .md-det-label {
          font-size: 10px;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--text-muted);
          margin-bottom: 8px;
          font-weight: 600;
        }
        .md-visit-date-inline {
          display: flex;
          gap: 6px;
          align-items: center;
        }
        .md-visit-year-input {
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.12);
          border-radius: 6px;
          color: var(--text-primary);
          padding: 4px 8px;
          font-size: 13px;
          width: 72px;
          outline: none;
          font-family: 'Inter', system-ui, sans-serif;
          transition: border-color 0.18s;
        }
        .md-visit-year-input::placeholder { color: rgba(255,255,255,0.3); }
        .md-visit-year-input:focus { border-color: var(--gold); }
        .md-visit-month-select {
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.12);
          border-radius: 6px;
          color: var(--text-primary);
          padding: 4px 6px;
          font-size: 13px;
          outline: none;
          font-family: 'Inter', system-ui, sans-serif;
          cursor: pointer;
          transition: border-color 0.18s;
        }
        .md-visit-month-select:focus { border-color: var(--gold); }
        .md-det-year-input {
          background: var(--surface);
          border: 1px solid var(--border-strong);
          border-radius: 4px;
          color: var(--text-primary);
          padding: 7px 10px;
          font-size: 14px;
          width: 110px;
          outline: none;
          font-family: 'Inter', system-ui, sans-serif;
          transition: border-color 0.18s;
        }
        .md-det-year-input:focus { border-color: var(--gold); }
        .md-det-preset-row { display: flex; gap: 8px; }
        .md-det-preset-btn {
          flex: 1;
          padding: 8px 0;
          border-radius: 2px;
          border: 1px solid var(--border-strong);
          background: var(--surface-2);
          color: var(--text-secondary);
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.18s;
          font-family: 'Inter', system-ui, sans-serif;
        }
        .md-det-preset-btn:hover { border-color: var(--border-strong); background: var(--surface-3); }
        .md-det-preset-btn.active {
          background: var(--black);
          color: var(--surface);
          border-color: var(--black);
          font-weight: 600;
        }
        .md-det-companion-chips { display: flex; flex-wrap: wrap; gap: 6px; margin: 8px 0; }
        .md-det-companion-chip {
          display: flex; align-items: center; gap: 5px;
          padding: 4px 8px 4px 10px; border-radius: 16px;
          background: rgba(201,168,76,0.08); border: 1px solid rgba(201,168,76,0.35);
          color: var(--gold-dim); font-size: 13px;
        }
        .md-det-chip-remove {
          background: none; border: none; cursor: pointer;
          color: inherit; opacity: 0.6; padding: 0; font-size: 14px;
        }
        .md-det-chip-remove:hover { opacity: 1; }
        .md-det-search-wrap { position: relative; }
        .md-det-search-input {
          width: 100%; background: var(--surface);
          border: 1px solid var(--border-strong); border-radius: 4px;
          color: var(--text-primary); padding: 8px 12px; font-size: 13px;
          outline: none; box-sizing: border-box;
          transition: border-color 0.18s;
          font-family: 'Inter', system-ui, sans-serif;
        }
        .md-det-search-input:focus { border-color: var(--gold); }
        .md-det-search-input::placeholder { color: var(--text-muted); }
        .md-det-search-dropdown {
          position: absolute; top: calc(100% + 4px); left: 0; right: 0;
          background: var(--surface); border: 1px solid var(--border);
          border-radius: 4px; overflow: hidden; z-index: 200;
          box-shadow: 0 8px 24px rgba(10,10,10,0.10);
        }
        .md-det-search-result {
          display: flex; align-items: center; gap: 9px;
          padding: 9px 12px; cursor: pointer; transition: background 0.12s;
          color: var(--text-primary);
        }
        .md-det-search-result:hover { background: var(--surface-2); }
        .md-det-result-avatar {
          width: 28px; height: 28px; border-radius: 50%;
          background: var(--surface-3); border: 1px solid var(--border);
          display: flex; align-items: center;
          justify-content: center; font-size: 12px; overflow: hidden; flex-shrink: 0;
          color: var(--gold-dim);
        }
        .md-det-result-avatar img { width: 100%; height: 100%; object-fit: cover; }
        .md-det-no-results { padding: 10px 12px; font-size: 12px; color: var(--text-muted); }
        .md-det-add-btn {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 6px 12px; border-radius: 2px;
          border: 1px dashed var(--border-strong); background: none;
          color: var(--text-muted); font-size: 11px; font-weight: 500;
          letter-spacing: 0.06em; text-transform: uppercase;
          cursor: pointer; transition: all 0.15s;
        }
        .md-det-add-btn:hover { border-color: var(--gold); color: var(--gold-dim); }
        .md-det-tags-toggle {
          display: inline-flex; align-items: center; gap: 5px;
          background: none; border: 1px dashed var(--border-strong);
          color: var(--text-muted); font-size: 11px; font-weight: 500;
          letter-spacing: 0.06em; text-transform: uppercase;
          padding: 5px 10px; border-radius: 2px; cursor: pointer;
          transition: all 0.15s; margin-top: 6px;
        }
        .md-det-tags-toggle:hover { border-color: var(--gold); color: var(--gold-dim); }
        .md-details-actions {
          display: flex; gap: 8px; align-items: center;
          padding-top: 4px;
        }
        .md-det-save-btn {
          padding: 9px 20px; border-radius: 2px;
          background: var(--black); color: var(--surface);
          border: none; font-size: 11px; font-weight: 600;
          cursor: pointer; letter-spacing: 0.08em; text-transform: uppercase;
          transition: background 0.15s;
        }
        .md-det-save-btn:hover { background: #222; }
        .md-det-save-btn:disabled { opacity: 0.4; cursor: default; }
        .md-det-cancel-btn {
          padding: 9px 14px; border-radius: 2px; background: none;
          color: var(--text-muted); border: 1px solid var(--border-strong);
          font-size: 11px; letter-spacing: 0.06em; text-transform: uppercase;
          cursor: pointer; transition: color 0.15s;
        }
        .md-det-cancel-btn:hover { color: var(--text-primary); }
        .md-det-saved { font-size: 12px; color: var(--gold-dim); letter-spacing: 0.04em; }
        .md-det-error { font-size: 12px; color: #C0392B; }
      `}</style>

      {/* Backdrop — hidden in map mode so the map stays visible */}
      {!noBackdrop && (
        <div
          className={`md-backdrop${isOpen ? ' md-backdrop-visible' : ''}`}
          onClick={onClose}
        />
      )}

      {/* Panel */}
      <aside className={`md-panel${isOpen ? ' md-panel-open' : ''}`}>

        {/* Header */}
        <div className="md-header">
          {!readOnly && (
            <button className="md-archive-btn" onClick={handleArchive} title="Archive this memory">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path d="M2 4h12M2 4v9a1 1 0 001 1h10a1 1 0 001-1V4M5 4V3a1 1 0 011-1h4a1 1 0 011 1v1M6.5 7v4M9.5 7v4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
              </svg>
            </button>
          )}
          <button className="md-close" onClick={onClose} aria-label="Close" style={{ position: 'absolute', top: 12, left: 12, zIndex: 10 }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M2 2l12 12M14 2L2 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>

          {/* Hero image / carousel */}
          {(() => {
            // Order photos so the active cover (localImageUrl) appears first.
            const orderedPhotos = orderPhotosCoverFirst(photos, localImageUrl);
            // Active photo index is bounded against the (possibly reordered) list.
            const activeIdx = Math.max(0, Math.min(photoIndex, orderedPhotos.length - 1));

            // Multi-photo: render true horizontal carousel.
            if (orderedPhotos.length >= 2) {
              const scrollToSlide = (i) => {
                const node = slideRefs.current[i];
                if (node && node.scrollIntoView) {
                  node.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
                }
              };
              return (
                <div
                  className={`md-hero-img md-carousel${generatingPhoto ? ' md-hero-generating' : ''}`}
                >
                  <div
                    className="md-carousel-track"
                    ref={carouselRef}
                    onScroll={(e) => {
                      const el = e.currentTarget;
                      const w = el.clientWidth || 1;
                      const i = Math.round(el.scrollLeft / w);
                      // Clamp to real photos (ignore the "+ Add more" tile for the dot indicator).
                      const clamped = Math.max(0, Math.min(i, orderedPhotos.length - 1));
                      if (clamped !== photoIndex) setPhotoIndex(clamped);
                    }}
                  >
                    {orderedPhotos.map((p, i) => (
                      <div
                        key={p.id || i}
                        ref={el => { slideRefs.current[i] = el; }}
                        className="md-carousel-slide"
                        style={{ backgroundImage: `url(${p.photoUrl})` }}
                      >
                        {!readOnly && (
                          <div className="md-photo-menu-wrap">
                            <button
                              className="md-photo-menu-btn"
                              onClick={() => setOpenPhotoMenu(openPhotoMenu === p.id ? null : p.id)}
                              aria-label="Photo actions"
                              title="Photo actions"
                            >⋯</button>
                            {openPhotoMenu === p.id && (
                              <div className="md-photo-menu" onMouseLeave={() => setOpenPhotoMenu(null)}>
                                <button
                                  className="md-photo-menu-item"
                                  onClick={() => handleSetCover(p)}
                                  disabled={localImageUrl === p.photoUrl}
                                >
                                  {localImageUrl === p.photoUrl ? '✓ Cover' : 'Set as cover'}
                                </button>
                                <button
                                  className="md-photo-menu-item md-photo-menu-item-danger"
                                  onClick={() => handlePhotoDelete(p.id)}
                                >Remove</button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                    {!readOnly && (
                      <div
                        ref={el => { slideRefs.current[orderedPhotos.length] = el; }}
                        className="md-carousel-slide md-carousel-add-tile"
                        onClick={() => fileInputRef.current?.click()}
                        role="button"
                        tabIndex={0}
                        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
                        title="Add more photos"
                      >
                        <span className="md-carousel-add-icon">+</span>
                        <span className="md-carousel-add-label">{uploadingPhotos ? 'Uploading…' : 'Add more'}</span>
                      </div>
                    )}
                  </div>
                  <button
                    className="md-carousel-prev"
                    onClick={() => { const ni = (activeIdx - 1 + orderedPhotos.length) % orderedPhotos.length; setPhotoIndex(ni); scrollToSlide(ni); }}
                    aria-label="Previous photo"
                  >&#8249;</button>
                  <button
                    className="md-carousel-next"
                    onClick={() => { const max = orderedPhotos.length + (readOnly ? 0 : 1); const ni = (activeIdx + 1) % max; setPhotoIndex(ni >= orderedPhotos.length ? 0 : ni); scrollToSlide(ni); }}
                    aria-label="Next photo"
                  >&#8250;</button>
                  <div className="md-carousel-dots">
                    {orderedPhotos.map((_, i) => (
                      <button
                        key={i}
                        className={`md-carousel-dot${i === activeIdx ? ' active' : ''}`}
                        onClick={() => { setPhotoIndex(i); scrollToSlide(i); }}
                        aria-label={`Photo ${i + 1}`}
                      />
                    ))}
                  </div>
                </div>
              );
            }

            // Single photo (uploaded or cover) — keep the simple background-image hero.
            const heroUrl = orderedPhotos.length === 1
              ? orderedPhotos[0].photoUrl
              : localImageUrl;
            return heroUrl ? (
              <div
                className={`md-hero-img${generatingPhoto ? ' md-hero-generating' : ''}`}
                style={{ backgroundImage: `url(${heroUrl})` }}
              />
            ) : (
              <div
                className={`md-hero-gradient${generatingPhoto ? ' md-hero-generating' : ''}`}
                style={{
                  background: pin.gradientStart && pin.gradientEnd
                    ? `linear-gradient(135deg, ${pin.gradientStart}, ${pin.gradientEnd})`
                    : 'linear-gradient(135deg, #4a3728, #8B6914)',
                }}
              >
                <span className="md-hero-emoji">{pin.emoji || '🌍'}</span>
              </div>
            );
          })()}

          {/* Hidden file input — shared by overlay button + carousel "+ Add more" tile */}
          {!readOnly && (
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: 'none' }}
              onChange={handlePhotoUpload}
            />
          )}

          {/* Cover-photo controls — bottom-right overlay (regenerate / unsplash prompt). */}
          {!readOnly && (
            <div className="md-photo-actions md-photo-actions-cover">
              {generatingPhoto ? (
                <div className="md-regen-photo-btn" style={{ cursor: 'default' }}>
                  <span className="md-regen-spinner" /> Finding photo…
                </div>
              ) : photoPromptOpen ? (
                <div className="md-photo-prompt">
                  <input
                    className="md-photo-prompt-input"
                    placeholder="e.g. Eiffel Tower at night, beach sunset…"
                    value={photoQuery}
                    onChange={e => setPhotoQuery(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') { e.preventDefault(); handleUnsplashPhoto(photoQuery.trim() || null); }
                      if (e.key === 'Escape') { setPhotoPromptOpen(false); setPhotoQuery(''); }
                    }}
                    autoFocus
                  />
                  <button className="md-photo-prompt-go" onClick={() => handleUnsplashPhoto(photoQuery.trim() || null)}>
                    Find
                  </button>
                  <button className="md-photo-prompt-go" style={{ background: 'none', color: 'rgba(255,255,255,0.5)' }} onClick={() => { setPhotoPromptOpen(false); setPhotoQuery(''); }}>
                    ✕
                  </button>
                </div>
              ) : (
                <button className="md-regen-photo-btn" onClick={() => setPhotoPromptOpen(true)} title="Change cover photo">
                  📷 Change photo
                </button>
              )}
            </div>
          )}

          {/* Add-photos affordance — separate row, bottom-LEFT, only when there are < 2 photos.
              For 2+ photos the "+ Add more" tile inside the carousel handles uploads. */}
          {!readOnly && photos.length < 2 && !photoPromptOpen && !generatingPhoto && (
            <div className="md-photo-actions md-photo-actions-upload">
              <button
                className="md-regen-photo-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPhotos}
                title="Upload your own photos"
              >
                {uploadingPhotos ? 'Uploading…' : '📎 Add photos'}
              </button>
            </div>
          )}
        </div>

        {/* Body */}
        <div className="md-body">

          {/* Place + meta */}
          <div className="md-title-block">
            {rank != null && (
              <div className="md-rank-badge">#{rank}</div>
            )}
            {!readOnly && editingTitle ? (
              <div className="md-title-edit">
                <input
                  ref={titleInputRef}
                  className="md-title-input"
                  value={titleText}
                  onChange={e => setTitleText(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') { e.preventDefault(); handleSaveTitle(); }
                    if (e.key === 'Escape') { setEditingTitle(false); setTitleText(pin.placeName || ''); }
                  }}
                  onBlur={handleSaveTitle}
                  disabled={titleSaving}
                  autoFocus
                />
              </div>
            ) : (
              <h2
                className={`md-place${readOnly ? '' : ' md-place-editable'}`}
                onClick={readOnly ? undefined : () => { setEditingTitle(true); setTimeout(() => titleInputRef.current?.focus(), 0); }}
                title={readOnly ? undefined : 'Click to edit'}
              >
                {pin.placeName}
              </h2>
            )}
            <div className="md-meta-row" style={{ alignItems: 'center', gap: 12 }}>
              {/* Inline visit date — year input + month picker */}
              {!readOnly ? (
                <div className="md-visit-date-inline">
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className="md-visit-year-input"
                    value={editYear}
                    onChange={e => setEditYear(e.target.value.replace(/\D/g, ''))}
                    onBlur={() => saveVisitDate(editYear, editMonth)}
                    placeholder="Year"
                    maxLength={4}
                  />
                  <select
                    className="md-visit-month-select"
                    value={editMonth}
                    onChange={e => { setEditMonth(e.target.value); saveVisitDate(editYear, e.target.value); }}
                  >
                    <option value="">Month</option>
                    {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, i) => (
                      <option key={i+1} value={String(i+1)}>{m}</option>
                    ))}
                  </select>
                </div>
              ) : (
                pin.visitYear && <span className="md-meta-item">
                  {pin.visitMonth ? `${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][pin.visitMonth-1]} ` : ''}{pin.visitYear}
                </span>
              )}

              {/* Hearts — interactive only on own pins */}
              <div className="md-hearts-row">
                {[1, 2, 3, 4, 5].map(v => (
                  <button
                    key={v}
                    type="button"
                    className={`md-heart-btn ${v <= liveRating ? 'filled' : ''}`}
                    onClick={readOnly ? undefined : () => handleRatingClick(v)}
                    title={readOnly ? undefined : `Rate ${v}`}
                    style={readOnly ? { cursor: 'default' } : undefined}
                  >
                    {v <= liveRating ? '❤️' : '🫶'}
                  </button>
                ))}
                {ratingSaved && <span className="md-rating-saved">saved</span>}
              </div>
            </div>
          </div>

          {/* Countries */}
          <div className="md-picker-section">
            <p className="md-section-label" style={{ marginBottom: 8 }}>Countries</p>
            {/* Country chips — always shown (union of all sources) */}
            {effectiveCountries.length > 0 && (
              <div className="md-chip-list">
                {effectiveCountries.map(c => {
                  const isLocationOnly = !(pin.countries || []).includes(c) && pin.normalizedCountry !== c;
                  return (
                    <span key={c} className="md-picker-chip">
                      <span className="md-picker-chip-flag">{countryFlag(c) || '🌍'}</span>
                      {c}
                      {!readOnly && !isLocationOnly && (
                        <button
                          type="button"
                          className="md-picker-chip-remove"
                          onClick={() => handleRemoveCountry(c)}
                          title="Remove"
                        >×</button>
                      )}
                    </span>
                  );
                })}
              </div>
            )}
            {!readOnly && effectiveCountries.length === 0 && (
              <p style={{ fontSize: 12, color: 'rgba(250,250,250,0.3)', marginBottom: 8 }}>
                No countries yet — type to add one
              </p>
            )}
            {/* Autocomplete input (own pins only) */}
            {!readOnly && <div className="md-picker-input-wrap">
              <input
                type="text"
                className="md-picker-input"
                placeholder="Add a country…"
                value={countryInput}
                onChange={e => setCountryInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                    countryKb.handleKeyDown(e);
                  } else if (e.key === 'Enter') {
                    e.preventDefault();
                    const idx = countryKb.highlightedIndex >= 0 ? countryKb.highlightedIndex : 0;
                    if (countryMatches[idx]) handleAddCountry(countryMatches[idx]);
                  } else if (e.key === 'Escape') {
                    setCountryInput('');
                    countryKb.resetHighlight();
                  }
                }}
                autoComplete="off"
              />
              {countryMatches.length > 0 && (
                <div className="md-picker-dropdown">
                  {countryMatches.map((c, i) => (
                    <div
                      key={c}
                      className={`md-picker-option${i === countryKb.highlightedIndex ? ' md-picker-option-highlighted' : ''}`}
                      onMouseDown={e => { e.preventDefault(); handleAddCountry(c); }}
                      onMouseEnter={() => countryKb.setHighlightedIndex(i)}
                    >
                      <span className="md-picker-option-flag">{countryFlag(c) || '🌍'}</span>
                      {c}
                    </div>
                  ))}
                </div>
              )}
            </div>}
          </div>

          {/* Venues: national parks + ski resorts */}
          <VenuePicker
            type="national_park"
            value={localVenues.filter(v => v.type === 'national_park')}
            onChange={updated => handleVenuesChange([
              ...localVenues.filter(v => v.type !== 'national_park'),
              ...updated,
            ])}
            readOnly={readOnly}
          />
          <VenuePicker
            type="ski_resort"
            value={localVenues.filter(v => v.type === 'ski_resort')}
            onChange={updated => handleVenuesChange([
              ...localVenues.filter(v => v.type !== 'ski_resort'),
              ...updated,
            ])}
            readOnly={readOnly}
          />

          {/* Places */}
          <div className="md-picker-section">
            <p className="md-section-label" style={{ marginBottom: 8 }}>Places</p>
            {/* Existing stop locations */}
            {localLocations.length > 0 && (
              <div className="md-chip-list">
                {localLocations.map(loc => {
                  const locFlag = loc.normalizedCountry
                    ? countryFlag(loc.normalizedCountry)
                    : null;
                  return (
                    <span key={loc.id} className="md-picker-chip">
                      {locFlag && <span className="md-picker-chip-flag">{locFlag}</span>}
                      {loc.placeName}
                      <button
                        type="button"
                        className="md-picker-chip-remove"
                        onClick={() => handleRemovePlace(loc.id)}
                      >×</button>
                    </span>
                  );
                })}
              </div>
            )}
            {/* Google Places autocomplete input (own pins only) */}
            {!readOnly && <div className="md-picker-input-wrap">
              <input
                type="text"
                inputMode="text"
                className="md-picker-input"
                placeholder="Search for a place…"
                value={placesInput}
                onChange={e => setPlacesInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                    placesKb.handleKeyDown(e);
                  } else if (e.key === 'Enter') {
                    e.preventDefault();
                    const idx = placesKb.highlightedIndex >= 0 ? placesKb.highlightedIndex : 0;
                    if (placesResults[idx]) handleSelectPlace(placesResults[idx]);
                    else if (placesInput.trim()) handleAddPlaceManual();
                  } else if (e.key === 'Escape') {
                    setPlacesInput(''); setPlacesResults([]);
                    placesKb.resetHighlight();
                  }
                }}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
              />
              {(placesLoading || placesResults.length > 0) && (
                <div className="md-picker-dropdown">
                  {placesLoading && (
                    <div className="md-picker-loading">Searching…</div>
                  )}
                  {!placesLoading && placesResults.map((r, i) => (
                    <div
                      key={r.placeId}
                      className={`md-picker-option${i === placesKb.highlightedIndex ? ' md-picker-option-highlighted' : ''}`}
                      onMouseDown={e => { e.preventDefault(); handleSelectPlace(r); }}
                      onMouseEnter={() => placesKb.setHighlightedIndex(i)}
                    >
                      <span className="md-picker-option-icon">📍</span>
                      <span>
                        <span className="md-picker-option-main">{r.mainText}</span>
                        {r.secondaryText && (
                          <span className="md-picker-option-sub"> {r.secondaryText}</span>
                        )}
                      </span>
                    </div>
                  ))}
                  {!placesLoading && placesResults.length === 0 && placesInput.trim().length > 1 && (
                    <div
                      className="md-picker-option"
                      onMouseDown={e => { e.preventDefault(); handleAddPlaceManual(); }}
                    >
                      <span className="md-picker-option-icon">＋</span>
                      Add &ldquo;{placesInput.trim()}&rdquo;
                    </div>
                  )}
                </div>
              )}
            </div>}
          </div>

          {/* Social: friends who dream of going here */}
          {(annotation?.friendsDreaming?.length > 0) && (
            <div className="md-section md-social-section">
              <p className="md-section-label" style={{ marginBottom: 8 }}>
                {annotation.friendsDreaming.length === 1 ? '1 friend' : `${annotation.friendsDreaming.length} friends`} dream{annotation.friendsDreaming.length === 1 ? 's' : ''} of going here
              </p>
              <div className="md-social-friends">
                {annotation.friendsDreaming.slice(0, 6).map((f, i) => {
                  const name = f.displayName || f.display_name || '?';
                  return (
                    <div key={f.userId || f.id || i} className="md-social-friend" title={name}>
                      {f.avatarUrl || f.avatar_url
                        ? <img src={f.avatarUrl || f.avatar_url} alt={name} />
                        : <span>{name.charAt(0).toUpperCase()}</span>
                      }
                    </div>
                  );
                })}
                {annotation.friendsDreaming.length > 6 && (
                  <div className="md-social-friend md-social-friend-overflow">+{annotation.friendsDreaming.length - 6}</div>
                )}
              </div>
            </div>
          )}

          {/* Companions + tag-a-friend */}
          <div>
            {pin.companions && pin.companions.length > 0 && (
              <div className="md-chips-row" style={{ marginBottom: 6 }}>
                {pin.companions.map(c => (
                  <span key={c} className="md-chip">{c}</span>
                ))}
              </div>
            )}

            {/* Tag a friend inline flow — shared TagFriendPanel component */}
            <TagFriendPanel
              pinId={pin.id}
              companions={pin.companions || []}
              pinType="memory"
              onCompanionsChange={newList => {
                setEditCompanions(newList);
                if (onPinChanged) onPinChanged(pin.id, { companions: newList });
              }}
              open={showTagFriend}
              onOpen={() => setShowTagFriend(true)}
              onClose={() => setShowTagFriend(false)}
              readOnly={readOnly}
            />
          </div>

          {/* Tags */}
          {pin.tags && pin.tags.length > 0 && (
            <div className="md-chips-row">
              {pin.tags.map(t => (
                <span key={t.id || t.name} className="md-tag-chip">
                  {t.emoji ? `${t.emoji} ` : ''}{t.name}
                </span>
              ))}
            </div>
          )}

          {/* Show as memory — own memory pins only */}
          {!readOnly && pin.pinType === 'memory' && (
            <div className="md-section md-would-go-back-section">
              <div className="md-would-go-back-row">
                <span className="md-would-go-back-label">🗺 Show as a memory on my board</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={showAsMemory}
                  className={`md-switch${showAsMemory ? ' md-switch-on' : ''}`}
                  onClick={() => handleShowAsMemory(!showAsMemory)}
                  disabled={showAsMemorySaving}
                  title={showAsMemory ? 'Showing on your Memories board' : 'Only in your Trip Timeline'}
                >
                  <span className="md-switch-knob" />
                </button>
              </div>
            </div>
          )}

          {/* Would go back — own pins only */}
          {!readOnly && (
            <div className="md-section md-would-go-back-section">
              <div className="md-would-go-back-row">
                <span className="md-would-go-back-label">↩ I want to go back</span>
                <div className="md-would-go-back-btns">
                  <button
                    type="button"
                    className={`md-wgb-btn${wouldGoBack === true ? ' md-wgb-btn-active' : ''}`}
                    onClick={() => handleWouldGoBack(wouldGoBack === true ? null : true)}
                    disabled={wouldGoBackSaving}
                    title="Yes, I want to return here"
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    className={`md-wgb-btn${wouldGoBack === false ? ' md-wgb-btn-no' : ''}`}
                    onClick={() => handleWouldGoBack(wouldGoBack === false ? null : false)}
                    disabled={wouldGoBackSaving}
                    title="Not particularly"
                  >
                    Not really
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Highlights — always visible, inline edit via pencil */}
          <div className="md-section">
            <div className="md-highlights-header">
              <p className="md-section-label" style={{ marginBottom: 0 }}>Highlights</p>
              {!readOnly && !editingHighlights && (
                <button
                  className="md-highlights-edit-btn"
                  onClick={() => {
                    setHighlightsText(pin.aiSummary || '');
                    setEditingHighlights(true);
                  }}
                >
                  <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                    <path d="M8.5 1.5l2 2L3 11H1v-2L8.5 1.5z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Edit
                </button>
              )}
            </div>

            {editingHighlights ? (
              <>
                <textarea
                  autoFocus
                  className="md-highlights-textarea"
                  value={highlightsText}
                  onChange={e => setHighlightsText(e.target.value)}
                  rows={5}
                  placeholder="- What made it special&#10;- Key moments"
                />
                <div className="md-highlights-actions">
                  <button
                    className="md-hl-cancel-btn"
                    onClick={() => setEditingHighlights(false)}
                    disabled={highlightsSaving}
                  >
                    Cancel
                  </button>
                  <button
                    className="md-hl-save-btn"
                    onClick={handleSaveHighlights}
                    disabled={highlightsSaving}
                  >
                    {highlightsSaving ? 'Saving…' : 'Done'}
                  </button>
                  {highlightsError && <span className="md-hl-error">{highlightsError}</span>}
                </div>
              </>
            ) : pin.aiSummary ? (
              summaryBullets ? (
                <ul className="md-bullet-list">
                  {summaryBullets.map((item, i) => <li key={i}>{item}</li>)}
                </ul>
              ) : (
                <p className="md-body-text">{pin.aiSummary}</p>
              )
            ) : (
              <button
                className="md-highlights-edit-btn"
                style={{ color: 'rgba(250,250,250,0.25)', fontSize: 13, padding: '2px 0' }}
                onClick={() => { setHighlightsText(''); setEditingHighlights(true); }}
              >
                + Add highlights…
              </button>
            )}
          </div>

          {/* Note */}
          {pin.note && (
            <div className="md-section">
              <p className="md-section-label">Notes</p>
              <p className="md-body-text md-note-text">{pin.note}</p>
            </div>
          )}

          {/* Add note (own pins only) */}
          {!readOnly && <div className="md-section md-add-section">
            <p className="md-section-label">Add to this memory</p>
            <textarea
              ref={textareaRef}
              className="md-textarea"
              value={addition}
              onChange={e => setAddition(e.target.value)}
              placeholder="More details, a follow-up thought, something you forgot to mention…"
              rows={3}
            />
            <div className="md-add-actions">
              {saveError && <span className="md-save-error">{saveError}</span>}
              {saved && <span className="md-save-ok">Saved</span>}
              <button
                className="md-save-btn"
                onClick={handleSaveAddition}
                disabled={saving}
              >
                {saving ? 'Saving…' : (addition.trim() ? 'Save' : 'Done')}
              </button>
            </div>
          </div>}

          {/* Edit details toggle (year, companions, tags) — own pins only */}
          {!readOnly && <button
            className={`md-details-toggle${showDetails ? ' open' : ''}`}
            onClick={() => setShowDetails(v => !v)}
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path d="M2 3.5l3 3 3-3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Edit details
          </button>}

          {!readOnly && showDetails && (
            <div className="md-details-expand">

              {/* Companions */}
              <div>
                <p className="md-det-label">With whom</p>
                <div className="md-det-preset-row" style={{ marginBottom: 8 }}>
                  <button
                    type="button"
                    className={`md-det-preset-btn ${isPresetActive('Solo') ? 'active' : ''}`}
                    onClick={() => togglePreset('Solo')}
                  >🧍 Solo</button>
                  <button
                    type="button"
                    className={`md-det-preset-btn ${isPresetActive('Family') ? 'active' : ''}`}
                    onClick={() => togglePreset('Family')}
                  >👨‍👩‍👧 Family</button>
                </div>

                {editCompanions.filter(c => c !== 'Solo' && c !== 'Family').length > 0 && (
                  <div className="md-det-companion-chips">
                    {editCompanions.filter(c => c !== 'Solo' && c !== 'Family').map(c => (
                      <span key={c} className="md-det-companion-chip">
                        {c}
                        <button type="button" className="md-det-chip-remove" onClick={() => removeCompanion(c)}>×</button>
                      </span>
                    ))}
                  </div>
                )}

                {showCompanionSearch ? (
                  <div className="md-det-search-wrap">
                    <input
                      autoFocus
                      type="text"
                      className="md-det-search-input"
                      placeholder="Search by name or username…"
                      value={companionSearch}
                      onChange={e => setCompanionSearch(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Escape') { setShowCompanionSearch(false); setCompanionSearch(''); }
                      }}
                    />
                    {companionSearch.trim().length > 0 && (
                      <div className="md-det-search-dropdown">
                        {companionSearching && <div className="md-det-no-results">Searching…</div>}
                        {!companionSearching && companionResults.map(user => (
                          <div key={user.id} className="md-det-search-result" onClick={() => addUserCompanion(user)}>
                            <div className="md-det-result-avatar">
                              {user.avatar_url
                                ? <img src={user.avatar_url} alt={user.display_name} />
                                : (user.display_name || user.username || '?')[0].toUpperCase()
                              }
                            </div>
                            <div>
                              <div style={{ fontSize: 13, color: 'rgba(250,250,250,0.9)', fontWeight: 500 }}>
                                {user.display_name || user.username}
                              </div>
                              {user.username && (
                                <div style={{ fontSize: 11, color: 'rgba(250,250,250,0.4)' }}>@{user.username}</div>
                              )}
                            </div>
                          </div>
                        ))}
                        {noCompanionResults && <div className="md-det-no-results">No users found</div>}
                      </div>
                    )}
                  </div>
                ) : (
                  <button type="button" className="md-det-add-btn" onClick={() => setShowCompanionSearch(true)}>
                    + Add a friend
                  </button>
                )}
              </div>

              {/* Tags */}
              <div>
                <p className="md-det-label">Tags</p>
                {editTags.length > 0 && !showTagPicker && (
                  <div className="md-chips-row" style={{ marginBottom: 6 }}>
                    {editTags.map(t => (
                      <span key={t} className="md-tag-chip">{t}</span>
                    ))}
                  </div>
                )}
                {showTagPicker ? (
                  <>
                    <TagPicker selectedTags={editTags} onTagsChange={setEditTags} />
                    <button
                      type="button"
                      className="md-det-tags-toggle"
                      style={{ marginTop: 8 }}
                      onClick={() => setShowTagPicker(false)}
                    >
                      Done
                    </button>
                  </>
                ) : (
                  <button type="button" className="md-det-tags-toggle" onClick={() => setShowTagPicker(true)}>
                    ✏️ Edit tags
                  </button>
                )}
              </div>

              {/* Save/cancel */}
              <div className="md-details-actions">
                <button
                  type="button"
                  className="md-det-cancel-btn"
                  onClick={() => {
                    setShowDetails(false);
                    setEditYear(pin.visitYear ? String(pin.visitYear) : '');
                    setEditCompanions(pin.companions || []);
                    setEditTags(pin.tags ? pin.tags.map(t => t.name || t) : []);
                    setShowTagPicker(false);
                    setShowCompanionSearch(false);
                    setCompanionSearch('');
                    setDetailsError('');
                  }}
                  disabled={detailsSaving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="md-det-save-btn"
                  onClick={handleSaveDetails}
                  disabled={detailsSaving}
                >
                  {detailsSaving ? 'Saving…' : 'Save changes'}
                </button>
                {detailsSaved && <span className="md-det-saved">Saved ✓</span>}
                {detailsError && <span className="md-det-error">{detailsError}</span>}
              </div>
            </div>
          )}

          {/* Transcript (collapsible) */}
          {pin.transcript && (
            <div className="md-section" style={{ marginTop: 16 }}>
              <button
                className="md-transcript-toggle"
                onClick={() => setShowTranscript(v => !v)}
              >
                {showTranscript ? 'Hide transcript' : 'Show original transcript'}
                <svg
                  width="12" height="12" viewBox="0 0 12 12" fill="none"
                  style={{ transform: showTranscript ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
                >
                  <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {showTranscript && (
                <p className="md-transcript-text">{pin.transcript}</p>
              )}
            </div>
          )}

          {/* Unsplash attribution */}
          {pin.unsplashAttribution && (
            <p className="md-attribution">{pin.unsplashAttribution}</p>
          )}
        </div>
      </aside>
    </>
  );
}
